import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AudioLines,
  AudioLinesX,
  Hourglass,
} from 'lucide-react';
import OptimizedImage from './OptimizedImage';
import {
  APP_STORE_URL,
  artworkForMeditation,
  CANONICAL_ORIGIN,
  canonicalMeditationURL,
  fetchMeditationPreview,
  formatCategory,
  formatDuration,
  formatVoice,
  getMeditationPreviewBootstrap,
  isMeditationID,
  MeditationPreview,
  MeditationPreviewStatus,
  normalizeMeditationID,
} from '../lib/meditationPreview';

type PreviewStatus = MeditationPreviewStatus | 'loading';

const setMetaContent = (attribute: 'name' | 'property', key: string, content: string) => {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.content = content;
};

const setCanonicalLink = (href: string) => {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }

  link.href = href;
};

const MetadataItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  alignment: 'start' | 'center' | 'end';
}> = ({ icon, label, alignment }) => {
  const alignmentClass = alignment === 'start'
    ? 'justify-start'
    : alignment === 'end'
      ? 'justify-end'
      : 'justify-center';

  return (
    <span className={`flex min-w-0 flex-1 items-center gap-2 text-sm font-medium text-white ${alignmentClass}`}>
      {icon}
      <span className="whitespace-nowrap">{label}</span>
    </span>
  );
};

const MeditationIcon: React.FC = () => (
  <span
    className="h-4 w-4 shrink-0 bg-current"
    style={{
      WebkitMask: 'url(/icons/meditation.svg) center / contain no-repeat',
      mask: 'url(/icons/meditation.svg) center / contain no-repeat',
    }}
    aria-hidden="true"
  />
);

const MeditationPreviewPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const meditationID = normalizeMeditationID(id);
  const initialBootstrap = getMeditationPreviewBootstrap(meditationID);
  const [item, setItem] = useState<MeditationPreview | null>(initialBootstrap?.item ?? null);
  const [status, setStatus] = useState<PreviewStatus>(
    initialBootstrap?.status ?? (isMeditationID(meditationID) ? 'loading' : 'missing')
  );
  const [artworkFailed, setArtworkFailed] = useState(false);

  useEffect(() => {
    const bootstrap = getMeditationPreviewBootstrap(meditationID);
    setArtworkFailed(false);

    if (bootstrap) {
      setItem(bootstrap.item);
      setStatus(bootstrap.status);
      return;
    }

    setItem(null);

    if (!isMeditationID(meditationID)) {
      setStatus('missing');
      return;
    }

    setStatus('loading');
    let cancelled = false;

    void fetchMeditationPreview(meditationID)
      .then((preview) => {
        if (cancelled) return;
        setItem(preview);
        setStatus(preview ? 'ready' : 'missing');
      })
      .catch(() => {
        if (!cancelled) setStatus('unavailable');
      });

    return () => {
      cancelled = true;
    };
  }, [meditationID]);

  const artwork = artworkForMeditation(meditationID, item);
  const fallbackArtwork = artworkForMeditation('');
  const visibleArtwork = artworkFailed ? fallbackArtwork : artwork;
  const previewURL = isMeditationID(meditationID)
    ? canonicalMeditationURL(meditationID)
    : `${CANONICAL_ORIGIN}/meditations`;
  const title = item ? `${item.title} | Nura` : 'Meditation | Nura';
  const description = item?.description ?? 'Explore a meditation from the Nura Gallery.';
  const absoluteArtworkURL = new URL(visibleArtwork.path, CANONICAL_ORIGIN).toString();
  const heroTitle = item?.title
    ?? (status === 'unavailable'
      ? 'Meditation unavailable'
      : status === 'missing'
        ? 'Meditation not found'
        : 'Nura meditation');

  useEffect(() => {
    document.title = title;
    setCanonicalLink(previewURL);
    setMetaContent('name', 'description', description);
    setMetaContent('property', 'og:type', 'article');
    setMetaContent('property', 'og:title', title);
    setMetaContent('property', 'og:description', description);
    setMetaContent('property', 'og:url', previewURL);
    setMetaContent('property', 'og:image', absoluteArtworkURL);
    setMetaContent('property', 'og:image:alt', item?.title ?? 'Nura meditation artwork');
    setMetaContent('name', 'twitter:card', 'summary_large_image');
    setMetaContent('name', 'twitter:title', title);
    setMetaContent('name', 'twitter:description', description);
    setMetaContent('name', 'twitter:image', absoluteArtworkURL);
    setMetaContent(
      'name',
      'apple-itunes-app',
      `app-id=6756171302, app-argument=${previewURL}`
    );
  }, [absoluteArtworkURL, description, item?.title, previewURL, title]);

  return (
    <main
      id="main-content"
      className="w-full bg-canvas text-text-primary"
      aria-busy={status === 'loading' ? 'true' : undefined}
    >
      <section
        id="hero"
        className="relative min-h-screen-fixed w-full overflow-hidden bg-black px-6 text-white md:min-h-screen md:px-16 lg:px-24"
      >
        <OptimizedImage
          src={visibleArtwork.path}
          blurSrc={visibleArtwork.blurPath}
          alt={item ? `${item.title} artwork` : 'Nura meditation artwork'}
          priority={true}
          containerClassName="absolute inset-0 w-full h-full z-0"
          className="absolute inset-0 h-full w-full scale-[1.04] object-cover object-center blur-[10px]"
        />
        <div
          className="absolute inset-0 z-[1]"
          style={{
            background:
              'linear-gradient(to bottom, transparent 0%, transparent 25%, rgba(0, 0, 0, 1) 68%, rgba(0, 0, 0, 1) 100%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex min-h-screen-fixed w-full max-w-5xl flex-col pb-8 pt-28 md:min-h-screen md:pb-12 md:pt-32">
          <header>
            <h1 className="max-w-3xl text-3xl font-medium leading-tight tracking-[-0.02em] text-white md:text-6xl lg:text-7xl">
              {heroTitle}
            </h1>
            {item?.subtitle && (
              <p className="mt-2 max-w-2xl text-[17px] leading-7 text-white/80 md:mt-4 md:text-xl">{item.subtitle}</p>
            )}
          </header>

          <div className="min-h-16 flex-1" aria-hidden="true" />

          {item ? (
            <div className="w-full max-w-2xl">
              <p className="text-[17px] leading-[1.65] text-white md:text-xl md:leading-8">{description}</p>

              <div className="mt-5 flex h-12 w-full items-center px-2 md:mt-7 md:max-w-sm">
                <MetadataItem
                  icon={<MeditationIcon />}
                  label={formatCategory(item.category)}
                  alignment="start"
                />
                <MetadataItem
                  icon={<Hourglass size={16} strokeWidth={1.75} aria-hidden="true" />}
                  label={formatDuration(item.duration_seconds)}
                  alignment="center"
                />
                <MetadataItem
                  icon={!item.narration_voice || item.narration_voice === 'none'
                    ? <AudioLinesX size={16} strokeWidth={1.75} aria-hidden="true" />
                    : <AudioLines size={16} strokeWidth={1.75} aria-hidden="true" />}
                  label={formatVoice(item.narration_voice)}
                  alignment="end"
                />
              </div>

              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 flex h-[52px] w-full items-center justify-center rounded-full bg-white px-6 text-[17px] font-medium text-black transition-opacity hover:opacity-85 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white md:mt-6 md:max-w-sm"
              >
                Continue in App →
              </a>
            </div>
          ) : (
            <div className="w-full max-w-2xl">
              <p className="text-[17px] leading-[1.65] text-white md:text-xl md:leading-8">
                {status === 'missing'
                  ? 'This meditation is not available on the website right now. Find the latest Nura experience in the app.'
                  : 'Open Nura to explore adaptive meditations designed for your current state.'}
              </p>
              <div className="mt-5 flex flex-wrap items-center gap-5 md:mt-7">
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-[52px] w-full items-center justify-center rounded-full bg-white px-6 text-[17px] font-medium text-black transition-opacity hover:opacity-85 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white md:max-w-sm"
                >
                  Continue in App →
                </a>
                <Link
                  to="/"
                  className="text-lg text-white underline underline-offset-4 focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
                >
                  Return home
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default MeditationPreviewPage;
